import type {
  ClusterTop,
  DrillLocation,
  FocusedActivationKey,
  StructuralLocation,
} from '../../types/exploration';
import { emptyDrillLocation, isDrillLocationEmpty } from './drillLocation';

const EC2_TOKEN = '$ec2';
const UNATTACHED_TOKEN = '$unattached';

function decodeSafe(raw: string): string | null {
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/** Structural-only URL serialization: no legacy string-array fallback (dev-only app, no back-compat need). */
export function serializeDrillLocation(location: DrillLocation): string | null {
  if (isDrillLocationEmpty(location)) return null;

  const parts: string[] = [];
  if (location.account) parts.push(`a:${encodeURIComponent(location.account)}`);

  const s = location.structural;
  if (s.kind === 'cluster') {
    const topToken =
      s.top.kind === 'cluster' ? s.top.name : s.top.kind === 'ec2' ? EC2_TOKEN : UNATTACHED_TOKEN;
    parts.push(`c:${encodeURIComponent(topToken)}`);
    if (s.nodeGroup) parts.push(`g:${encodeURIComponent(s.nodeGroup)}`);
    if (s.instanceId) parts.push(`i:${encodeURIComponent(s.instanceId)}`);
  } else if (s.kind === 'region') {
    if (s.region) parts.push(`r:${encodeURIComponent(s.region)}`);
    if (s.instanceId) parts.push(`i:${encodeURIComponent(s.instanceId)}`);
  } else if (s.kind === 'tag') {
    if (s.tagValue) parts.push(`t:${encodeURIComponent(s.tagValue)}`);
    if (s.instanceId) parts.push(`i:${encodeURIComponent(s.instanceId)}`);
  }

  if (location.statusLabel) parts.push(`s:${encodeURIComponent(location.statusLabel)}`);
  if (location.focusedActivationKey) parts.push(`f:${location.focusedActivationKey}`);
  if (location.reasonLabel) parts.push(`n:${encodeURIComponent(location.reasonLabel)}`);

  return parts.length ? parts.join('/') : null;
}

/**
 * Parses the structured `key:value/...` URL form. Any malformed or
 * unrecognized token falls back to the empty (account-root) location rather
 * than guessing — there is no ambiguous state left to repair into.
 */
export function parseDrillLocation(raw: string | null | undefined): DrillLocation {
  const empty = emptyDrillLocation();
  if (!raw) return empty;

  let account: string | undefined;
  let clusterTop: ClusterTop | undefined;
  let nodeGroup: string | undefined;
  let region: string | undefined;
  let tagValue: string | undefined;
  let instanceId: string | undefined;
  let statusLabel: string | undefined;
  let focusedActivationKey: FocusedActivationKey | undefined;
  let reasonLabel: string | undefined;
  let structuralKindSeen: 'cluster' | 'region' | 'tag' | null = null;

  for (const token of raw.split('/').filter(Boolean)) {
    const colon = token.indexOf(':');
    if (colon <= 0) return empty;
    const key = token.slice(0, colon);
    const value = decodeSafe(token.slice(colon + 1));
    if (!value) return empty;

    switch (key) {
      case 'a':
        account = value;
        break;
      case 'c':
        if (structuralKindSeen && structuralKindSeen !== 'cluster') return empty;
        structuralKindSeen = 'cluster';
        clusterTop =
          value === EC2_TOKEN
            ? { kind: 'ec2' }
            : value === UNATTACHED_TOKEN
              ? { kind: 'unattached' }
              : { kind: 'cluster', name: value };
        break;
      case 'g':
        if (structuralKindSeen !== 'cluster') return empty;
        nodeGroup = value;
        break;
      case 'r':
        if (structuralKindSeen && structuralKindSeen !== 'region') return empty;
        structuralKindSeen = 'region';
        region = value;
        break;
      case 't':
        if (structuralKindSeen && structuralKindSeen !== 'tag') return empty;
        structuralKindSeen = 'tag';
        tagValue = value;
        break;
      case 'i':
        if (!structuralKindSeen) return empty;
        instanceId = value;
        break;
      case 's':
        statusLabel = value;
        break;
      case 'f':
        if (value !== 'unsupported' && value !== 'potentiallySupported') return empty;
        focusedActivationKey = value;
        break;
      case 'n':
        reasonLabel = value;
        break;
      default:
        return empty;
    }
  }

  let structural: StructuralLocation;
  if (structuralKindSeen === 'cluster' && clusterTop) {
    structural = { kind: 'cluster', top: clusterTop, nodeGroup, instanceId };
  } else if (structuralKindSeen === 'region') {
    structural = { kind: 'region', region, instanceId };
  } else if (structuralKindSeen === 'tag') {
    structural = { kind: 'tag', tagValue, instanceId };
  } else {
    structural = { kind: 'empty' };
  }

  return { account, structural, statusLabel, focusedActivationKey, reasonLabel };
}
